import { artifactCategory, type ShowArtifact } from './creationShowModel.js';
import {
  CREATION_FILM_FALLBACK_CAPTIONS, normalizeCreationFilmCaption, publicCreationFilmCaption,
  type CreationFilmMaterialV1, type CreationFilmRunV1,
} from '@djgg/creations';

export const FILM_RECIPES = [
  'kinetic-title', 'contact-sheet', 'hero', 'parallax', 'lineup',
  'mechanic', 'cutout', 'deej', 'soundtrack', 'gameplay',
] as const;

export type FilmRecipe = typeof FILM_RECIPES[number];

export interface FilmArrival {
  id: string;
  /** Completed film scenes since this material arrived without screening it. */
  unscreenedScenes: number;
}

export interface FilmScene {
  id: string;
  index: number;
  recipe: FilmRecipe;
  compositionKey: string;
  artifactIds: string[];
  durationMs: number;
  layout: number;
  deejAsset?: string;
  reducedMotion: boolean;
  /** Sealed manifests may supply exact public edit copy. */
  title?: string;
  caption?: string;
}

export interface FilmDirectionInput {
  seed: string;
  index: number;
  title: string;
  artifacts: readonly ShowArtifact[];
  history: readonly FilmScene[];
  arrivals?: readonly FilmArrival[];
  reducedMotion?: boolean;
  finale?: boolean;
}

export const CREATION_TRAILER_DEEJ_ASSETS = [
  '/mascot/deej-film-spark-catcher.webp',
  '/mascot/deej-film-rule-cartographer.webp',
  '/mascot/deej-film-raw-art-receiver.webp',
  '/mascot/deej-film-cutout-technician.webp',
  '/mascot/deej-film-casting-director.webp',
  '/mascot/deej-film-beat-builder.webp',
  '/mascot/deej-film-crash-test-pilot.webp',
  '/mascot/deej-film-fix-mechanic.webp',
  '/mascot/deej-film-trailer-director.webp',
] as const;

export function creationTrailerDeejUrl(assetId: string | undefined): string | undefined {
  if (!assetId) return undefined;
  const url = `/mascot/${assetId}.webp`;
  return CREATION_TRAILER_DEEJ_ASSETS.find(item => item === url);
}

/** What decides which of a creation's film runs a surface is following. */
export type CreationFilmAuthority =
  /** A draft follows its current analysis run only: another run's seed or
   * failure belongs to an idea this draft has moved on from. */
  | { kind: 'analysis'; runId: string | null }
  | { kind: 'game'; manifestRunId?: string; versions: readonly number[] };

export function selectCreationFilmRun(
  runs: readonly CreationFilmRunV1[],
  authority: CreationFilmAuthority,
): CreationFilmRunV1 | null {
  if (authority.kind === 'analysis') return runs.find(run => run.runId === authority.runId) ?? null;
  const ordered = [...runs].sort((a, b) => b.updatedAt - a.updatedAt || b.runId.localeCompare(a.runId));
  const versions = new Set(authority.versions);
  return ordered.find(run => run.runId === authority.manifestRunId)
    ?? ordered.find(run => (run.binding.kind === 'version' && versions.has(run.binding.version))
      || (run.binding.kind === 'revision' && run.binding.version != null && versions.has(run.binding.version)))
    ?? ordered[0]
    ?? null;
}

/** When the show last moved: its own saved progress, or the film's newest
 * beat (every stored material bumps the film run), whichever is later. A
 * stage that keeps writing beats never reads as stalled. */
export function filmLastUpdatedAt(progressAt: number | null | undefined, run: Pick<CreationFilmRunV1, 'updatedAt'> | null | undefined): number | undefined {
  const times = [progressAt, run?.updatedAt].filter((value): value is number => typeof value === 'number');
  return times.length ? Math.max(...times) : undefined;
}

/** Live film materials join a show's artifacts, skipping anything already shown. */
export function appendFilmMaterialArtifacts(artifacts: ShowArtifact[], materials: readonly CreationFilmMaterialV1[]): void {
  for (const material of materials) {
    const id = `film:${material.materialId}`;
    if (artifacts.some(item => item.id === id || (material.uri && item.url === material.uri))) continue;
    const deejUrl = material.kind === 'deej' ? creationTrailerDeejUrl(material.deejAssetId) : undefined;
    const materialUrl = material.kind === 'deej' ? deejUrl : material.uri;
    const kind: ShowArtifact['kind'] = material.kind === 'soundtrack' ? 'audio'
      : (material.kind === 'gameplay-capture' || material.kind === 'playable-proof') && material.contentType?.startsWith('video/') ? 'video'
        : material.kind === 'gameplay-capture' || material.kind === 'playable-proof' ? 'capture'
          : material.kind === 'sprite-strip' ? 'animation'
            : materialUrl ? 'image' : 'discovery';
    const category: ShowArtifact['category'] = material.kind === 'title' || material.kind === 'cover' ? 'identity'
      : material.kind === 'soundtrack' ? 'sound'
        : material.kind === 'gameplay-capture' || material.kind === 'playable-proof' ? 'proof'
          : material.kind === 'cue' ? 'gameplay'
            : material.kind === 'sprite-strip' ? 'characters'
              : material.kind === 'processed-art' || material.kind === 'raw-preview' || material.kind === 'concept' ? 'world'
                : 'extras';
    artifacts.push({
      id, title: material.title, summary: material.description ?? material.text ?? '', kind,
      url: materialUrl, strip: material.strip, unavailable: !!material.unavailableReason || (material.kind === 'deej' && !deejUrl), category,
      gameAsset: GAME_ASSET_MATERIALS.has(material.kind),
      beat: category === 'proof' ? 'playable-proof' : category === 'gameplay' ? 'gameplay-hook' : undefined,
      origin: material.source === 'reference' || material.source === 'raw' ? 'reference'
        : material.source === 'version' ? 'existing' : 'current-run',
      state: material.unavailableReason ? 'unavailable' : 'recommended',
      label: material.unavailableReason ? 'Unavailable' : material.kind === 'deej' ? 'Deej in the workshop' : 'Creation film material',
    });
  }
}

const GAME_ASSET_MATERIALS: ReadonlySet<CreationFilmMaterialV1['kind']> = new Set([
  'cover', 'soundtrack', 'gameplay-capture', 'playable-proof', 'processed-art', 'sprite-strip',
]);

const VISUAL_RECIPES: readonly FilmRecipe[] = ['contact-sheet', 'hero', 'parallax', 'lineup', 'cutout', 'gameplay'];

export function filmSeed(identity: string, personalSeed: string): string {
  return hash(`${identity}\u0000${personalSeed}`).toString(36);
}

function hash(value: string): number {
  let result = 2166136261;
  for (let i = 0; i < value.length; i++) {
    result ^= value.charCodeAt(i);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

function score(seed: string, key: string): number {
  return hash(`${seed}:${key}`) / 0xffffffff;
}

function availableRecipes(artifacts: readonly ShowArtifact[]): FilmRecipe[] {
  const visual = artifacts.filter(isVisual);
  const characters = visual.filter(item => artifactCategory(item) === 'characters');
  const mechanics = artifacts.filter(item => item.kind === 'discovery' || artifactCategory(item) === 'gameplay');
  const audio = artifacts.filter(item => item.kind === 'audio' && item.url && !item.unavailable);
  const gameplay = artifacts.filter(item => item.kind === 'video' || item.kind === 'capture' || artifactCategory(item) === 'proof');
  return [
    'kinetic-title',
    ...(visual.length >= 2 ? ['contact-sheet'] as const : []),
    ...(visual.length ? ['hero'] as const : []),
    ...(visual.length >= 2 ? ['parallax'] as const : []),
    ...(visual.length >= 2 ? ['lineup'] as const : []),
    ...(mechanics.length ? ['mechanic'] as const : []),
    ...(characters.length ? ['cutout'] as const : []),
    'deej',
    ...(audio.length ? ['soundtrack'] as const : []),
    ...(gameplay.length ? ['gameplay'] as const : []),
  ];
}

/** How many recent scenes a shown picture set sits out while other material can play. */
const RECENT_PICTURE_SCENES = 3;

function isVisual(artifact: ShowArtifact): boolean {
  return !!artifact.url && !artifact.unavailable && artifact.kind !== 'audio' && artifact.kind !== 'discovery'
    && !artifact.url.startsWith('/mascot/deej-film-');
}

function pickArtifacts(recipe: FilmRecipe, artifacts: readonly ShowArtifact[], seed: string, index: number, arrival?: string): string[] {
  const visual = artifacts.filter(isVisual);
  const byScore = (items: readonly ShowArtifact[]) => [...items].sort((a, b) => score(seed, `${index}:${a.id}`) - score(seed, `${index}:${b.id}`));
  const urgent = arrival ? artifacts.find(item => item.id === arrival) : undefined;
  const withUrgent = (items: readonly ShowArtifact[], count: number) => {
    const ordered = byScore(items).filter(item => item.id !== urgent?.id);
    return [...(urgent && items.some(item => item.id === urgent.id) ? [urgent] : []), ...ordered].slice(0, count).map(item => item.id);
  };
  switch (recipe) {
    case 'kinetic-title':
      return withUrgent(artifacts.filter(item => artifactCategory(item) === 'identity' && isVisual(item)), 1);
    case 'contact-sheet': return withUrgent(visual, 4);
    case 'hero': return withUrgent(visual, 1);
    case 'parallax': return withUrgent(visual, 2);
    case 'lineup': {
      const grouped = visual.filter(item => item.group && visual.some(other => other.id !== item.id && other.group === item.group));
      return withUrgent(grouped.length >= 2 ? grouped : visual, 3);
    }
    case 'mechanic': return withUrgent(artifacts.filter(item => item.kind === 'discovery' || artifactCategory(item) === 'gameplay'), 1);
    case 'cutout': return withUrgent(visual.filter(item => artifactCategory(item) === 'characters'), 1);
    case 'soundtrack': {
      const soundtrack = artifacts.filter(item => item.kind === 'audio' && item.url && !item.unavailable);
      const identity = visual.filter(item => artifactCategory(item) === 'identity');
      return [...withUrgent(soundtrack, 1), ...withUrgent(identity, 1)].slice(0, 2);
    }
    case 'gameplay': return withUrgent(artifacts.filter(item => item.kind === 'video' || item.kind === 'capture' || artifactCategory(item) === 'proof'), 1);
    // Deej is a full-frame interlude. Keeping it free of artifacts gives the
    // artwork room to read and prevents the mascot from covering new material.
    case 'deej': return [];
  }
}

function canScreen(recipe: FilmRecipe, artifact: ShowArtifact): boolean {
  if (artifact.url?.includes('/mascot/deej-film-')) return false;
  if (artifact.kind === 'audio') return recipe === 'soundtrack';
  if (artifact.kind === 'discovery') return recipe === 'mechanic';
  if (artifact.kind === 'video' || artifact.kind === 'capture') return recipe === 'gameplay' || VISUAL_RECIPES.includes(recipe);
  return VISUAL_RECIPES.includes(recipe) || recipe === 'kinetic-title';
}

/**
 * Pure seeded director. It depends only on the current material inventory and
 * the retained twelve-scene history; no wall clock or random global is read.
 */
export function directFilmScene(input: FilmDirectionInput): FilmScene {
  const history = input.history.slice(-12);
  if (input.finale) {
    return buildScene('kinetic-title', input, undefined, 3_200, 'finale');
  }
  if (input.index === 0) return buildScene('kinetic-title', input, undefined, 4_600, 'opening');

  const previous = history.at(-1);
  const sinceDeej = (() => {
    let last = -1;
    for (let i = history.length - 1; i >= 0; i--) if (history[i]!.recipe === 'deej') { last = i; break; }
    return last < 0 ? history.length : history.length - last - 1;
  })();
  const deejAfter = 3 + (hash(`${input.seed}:deej:${input.index - sinceDeej}`) % 3);
  const urgent = [...(input.arrivals ?? [])]
    .filter(item => item.unscreenedScenes >= 1 && input.artifacts.some(artifact => artifact.id === item.id && !artifact.url?.includes('/mascot/deej-film-')))
    .sort((a, b) => b.unscreenedScenes - a.unscreenedScenes || a.id.localeCompare(b.id))[0];
  let candidates = availableRecipes(input.artifacts).filter(recipe => recipe !== previous?.recipe);
  if (urgent) {
    const material = input.artifacts.find(item => item.id === urgent.id)!;
    candidates = candidates.filter(recipe => canScreen(recipe, material));
  } else if (sinceDeej >= deejAfter && previous?.recipe !== 'deej') {
    candidates = ['deej'];
  } else {
    candidates = candidates.filter(recipe => recipe !== 'deej');
  }
  if (!candidates.length) candidates = previous?.recipe === 'kinetic-title' ? ['deej'] : ['kinetic-title'];
  candidates = [...candidates].sort((a, b) => score(input.seed, `${input.index}:${a}`) - score(input.seed, `${input.index}:${b}`));

  // A picture already on screen in the last few scenes waits while any other
  // material can play: one image must never hold the stage in new layouts.
  const recentPictures = new Set(history.slice(-RECENT_PICTURE_SCENES).flatMap(item => item.artifactIds.length ? [item.artifactIds.join(',')] : []));
  for (const fresh of [true, false]) {
    for (const recipe of candidates) {
      for (let layout = 0; layout < 6; layout++) {
        const scene = buildScene(recipe, input, urgent?.id, input.reducedMotion ? 4_200 : 3_100, String(layout));
        if (fresh && recentPictures.has(scene.artifactIds.join(','))) continue;
        if (!history.some(item => item.compositionKey === scene.compositionKey)) return scene;
      }
    }
  }
  return buildScene(candidates[0]!, input, urgent?.id, input.reducedMotion ? 4_200 : 3_100, `escape-${input.index}`);
}

function buildScene(
  recipe: FilmRecipe,
  input: FilmDirectionInput,
  arrival: string | undefined,
  durationMs: number,
  salt: string,
): FilmScene {
  const layout = hash(`${input.seed}:${input.index}:${recipe}:${salt}`) % 6;
  let artifactIds = pickArtifacts(recipe, input.artifacts, `${input.seed}:${salt}`, input.index, arrival);
  // A recipe may narrow its normal pool (for example, a lineup prefers a
  // repeated character group). A newly arrived compatible material still has
  // priority over that preference so realtime work is screened on time.
  const arrivalArtifact = arrival ? input.artifacts.find(item => item.id === arrival) : undefined;
  if (arrivalArtifact && canScreen(recipe, arrivalArtifact) && !artifactIds.includes(arrivalArtifact.id)) {
    artifactIds = [arrivalArtifact.id, ...artifactIds].slice(0, 4);
  }
  const deejAsset = recipe === 'deej'
    ? CREATION_TRAILER_DEEJ_ASSETS[hash(`${input.seed}:deej:${input.index}`) % CREATION_TRAILER_DEEJ_ASSETS.length]
    : undefined;
  const compositionKey = `${recipe}|${layout}|${artifactIds.join(',')}|${deejAsset ?? ''}`;
  const usedCaptions = new Set(input.history.slice(-12).flatMap(scene => scene.caption ? [normalizeCaption(scene.caption)] : []));
  const availableCopy = (values: readonly string[]) => values.map(publicCreationFilmCaption)
    .filter((value): value is string => !!value && !usedCaptions.has(normalizeCaption(value)))
    .sort((left, right) => score(input.seed, `${input.index}:caption:${left}`) - score(input.seed, `${input.index}:caption:${right}`));
  const caption = recipe === 'deej' || input.index === 0 ? undefined
    : availableCopy(CREATION_FILM_FALLBACK_CAPTIONS)[0];
  return {
    id: `${input.seed}:${input.index}:${hash(compositionKey).toString(36)}`,
    index: input.index,
    recipe,
    compositionKey,
    artifactIds,
    durationMs: Math.min(8_000, Math.max(2_500, durationMs)),
    layout,
    deejAsset,
    reducedMotion: !!input.reducedMotion,
    ...(caption ? { caption } : {}),
  };
}

function normalizeCaption(value: string): string {
  return normalizeCreationFilmCaption(value);
}

/** A sealed manifest can be played by the same renderer without redirection. */
export function filmArtifacts(scene: FilmScene, artifacts: readonly ShowArtifact[]): ShowArtifact[] {
  return scene.artifactIds.flatMap(id => {
    const artifact = artifacts.find(item => item.id === id);
    return artifact ? [artifact] : [];
  });
}
